import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Spinner, Tabs, Alert } from './shared';
import UserOverviewTab from './users/UserOverviewTab';
import UserSessionsTab from './users/UserSessionsTab';
import UserCostTab from './users/UserCostTab';
import UserContentTab from './users/UserContentTab';
import UserActivityTab from './users/UserActivityTab';
import UserInsightsTab from './users/UserInsightsTab';

const TIER_BADGES = {
    free: 'default',
    pro: 'info',
    enterprise: 'purple',
};

const fmt = (n) => {
    if (n == null) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

const fmtCost = (n) => {
    if (n == null) return '$0.0000';
    return `$${n.toFixed(4)}`;
};

const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

function StatCard({ label, value }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{value || '-'}</p>
        </div>
    );
}

export default function AdminUserDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'sessions', label: 'Sessions' },
        { id: 'cost', label: 'Cost & Usage' },
        { id: 'content', label: 'Content' },
        { id: 'activity', label: 'Activity' },
        { id: 'insights', label: 'Insights' },
    ];

    useEffect(() => {
        loadSummary();
    }, [userId]);

    const loadSummary = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminApi.getUserSummary(userId);
            setSummary(data);
        } catch (err) {
            console.error('Failed to load user summary:', err);
            setError(err.message || 'Failed to load user data');
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        navigate('/admin/user-tiers');
    };

    const renderTabContent = () => {
        if (!summary) return null;

        switch (activeTab) {
            case 'overview':
                return <UserOverviewTab userId={userId} />;
            case 'sessions':
                return <UserSessionsTab userId={userId} />;
            case 'cost':
                return <UserCostTab userId={userId} />;
            case 'content':
                return <UserContentTab userId={userId} />;
            case 'activity':
                return <UserActivityTab userId={userId} />;
            case 'insights':
                return <UserInsightsTab userId={userId} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner size="lg" text="Loading user data..." center />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Alert variant="error">{error}</Alert>
                <div className="mt-4">
                    <Button variant="ghost" onClick={goBack}>Back to User List</Button>
                </div>
            </div>
        );
    }

    if (!summary) {
        return (
            <div className="p-6">
                <Alert variant="error">User not found</Alert>
                <div className="mt-4">
                    <Button variant="ghost" onClick={goBack}>Back to User List</Button>
                </div>
            </div>
        );
    }

    const initials = userId ? userId.slice(0, 2).toUpperCase() : '?';

    return (
        <div className="min-h-screen bg-white">
            <div className="px-6 pt-4 pb-0">
                <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-4"
                >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    User Tiers
                </button>

                <div className="flex items-start gap-4 mb-5">
                    <div className="w-11 h-11 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
                        <span className="text-sm font-semibold text-white">{initials}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                            <h1 className="text-lg font-semibold text-gray-900 truncate">{userId}</h1>
                            <Badge variant={TIER_BADGES[summary.tier_name] || 'default'}>
                                {summary.tier_name}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                            {summary.last_active && (
                                <span>Active {fmtDate(summary.last_active)}</span>
                            )}
                            <span>{summary.quick_stats?.total_sessions || 0} sessions</span>
                            <span>{fmtCost(summary.quick_stats?.total_cost)}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <StatCard label="Sessions" value={summary.quick_stats?.total_sessions || 0} />
                    <StatCard label="Total Cost" value={fmtCost(summary.quick_stats?.total_cost)} />
                    <StatCard label="Uploads" value={summary.quick_stats?.total_uploads || 0} />
                    <StatCard label="Shares" value={summary.quick_stats?.total_shares || 0} />
                </div>
            </div>

            <div className="px-6 pt-2 pb-2 sticky top-0 z-10 bg-white">
                <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
            </div>

            <div className="px-6 py-6 min-h-[calc(100vh-400px)]">
                {renderTabContent()}
            </div>
        </div>
    );
}
